import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useApi } from '../hooks/useApi'
import { EmotionSlider } from '../components/EmotionSlider'
import { Loading } from '../components/Loading'
import { CrisisOverlay, detectCrisisKeywords } from '../components/CrisisOverlay'
import {
  trackSessionStarted,
  trackInputCompleted,
  trackAIStructured,
  trackSessionSaved,
  trackCrisisOverlayShown,
} from '../lib/analytics'
import { ContextualHint, HINTS } from '../components/ContextualHint'
import type { StructuredSession, SessionCreateInput } from '../types'

type Mode = 'voice' | 'text'
type Step = 'input' | 'processing' | 'review' | 'saving'

const DISTORTIONS = [
  'All-or-Nothing Thinking',
  'Overgeneralization',
  'Mental Filter',
  'Disqualifying the Positive',
  'Jumping to Conclusions',
  'Magnification',
  'Emotional Reasoning',
  'Should Statements',
  'Labeling',
  'Personalization',
  'Catastrophizing',
  'Mind Reading',
]

export default function NewSession() {
  const navigate = useNavigate()
  const { post } = useApi()

  const [mode, setMode] = useState<Mode>('text')
  const [step, setStep] = useState<Step>('input')
  const [textInput, setTextInput] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)

  // Structured session data
  const [session, setSession] = useState<Partial<StructuredSession>>({
    trigger_text: '',
    auto_thought: '',
    emotion_label: '',
    emotion_intensity_pre: 5,
    evidence_for: [],
    evidence_against: [],
    balanced_thought: '',
    action_step: '',
    distortions: [],
  })
  const [intensityPost, setIntensityPost] = useState(3)

  // Crisis detection
  const [showCrisisOverlay, setShowCrisisOverlay] = useState(false)
  const [crisisKeyword, setCrisisKeyword] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)

  // Detect crisis keywords in text input
  useEffect(() => {
    const textToCheck = textInput || session.trigger_text || session.auto_thought || ''
    const detected = detectCrisisKeywords(textToCheck)
    if (detected && !crisisKeyword) {
      setCrisisKeyword(detected)
      setShowCrisisOverlay(true)
      trackCrisisOverlayShown()
    }
  }, [textInput, session.trigger_text, session.auto_thought])

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop())
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        await processAudio(blob)
      }

      mediaRecorder.start()
      setIsRecording(true)
      setRecordingTime(0)
      trackSessionStarted('voice')

      timerRef.current = window.setInterval(() => {
        setRecordingTime(t => t + 1)
      }, 1000)
    } catch (error) {
      console.error('Failed to start recording:', error)
      alert('Could not access microphone. Please check permissions.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }

  const processAudio = async (blob: Blob) => {
    setStep('processing')

    try {
      // Convert to base64
      const reader = new FileReader()
      const base64 = await new Promise<string>((resolve) => {
        reader.onloadend = () => {
          const result = reader.result as string
          resolve(result.split(',')[1])
        }
        reader.readAsDataURL(blob)
      })

      // Transcribe
      const sttResult = await post<{ transcript: string }>('/api/stt', {
        audio: base64,
        format: 'webm',
      })

      // Structure with AI
      const structured = await post<StructuredSession>('/api/ai/structure-session', {
        transcript: sttResult.transcript,
      })

      trackInputCompleted('voice', recordingTime * 1000)
      trackAIStructured()

      setSession(structured)
      setStep('review')
    } catch (error) {
      console.error('Processing failed:', error)
      alert('Failed to process recording. Please try again.')
      setStep('input')
    }
  }

  const processText = async () => {
    if (!textInput.trim()) return

    trackSessionStarted('text')
    setStep('processing')

    try {
      const structured = await post<StructuredSession>('/api/ai/structure-session', {
        transcript: textInput,
      })

      trackInputCompleted('text')
      trackAIStructured()

      setSession(structured)
      setStep('review')
    } catch (error) {
      console.error('Processing failed:', error)
      alert('Failed to process text. Please try again.')
      setStep('input')
    }
  }

  const toggleDistortion = (distortion: string) => {
    setSession(prev => ({
      ...prev,
      distortions: prev.distortions?.includes(distortion)
        ? prev.distortions.filter(d => d !== distortion)
        : [...(prev.distortions || []), distortion],
    }))
  }

  const saveSession = async () => {
    setStep('saving')

    try {
      const input: SessionCreateInput = {
        mode,
        trigger_text: session.trigger_text || '',
        auto_thought: session.auto_thought || '',
        emotion_label: session.emotion_label || 'anxious',
        emotion_intensity_pre: session.emotion_intensity_pre || 5,
        emotion_intensity_post: intensityPost,
        evidence_for: session.evidence_for,
        evidence_against: session.evidence_against,
        balanced_thought: session.balanced_thought,
        action_step: session.action_step,
        time_orientation: session.time_orientation,
        distortions: session.distortions,
      }

      const result = await post<{ session: { id: string } }>('/api/sessions', input)

      // Track session saved
      const hasDistortions = (session.distortions?.length || 0) > 0
      const emotionDelta = (session.emotion_intensity_pre || 5) - intensityPost
      trackSessionSaved(hasDistortions, emotionDelta)

      navigate(`/session/${result.session.id}`)
    } catch (error) {
      console.error('Failed to save session:', error)
      alert('Failed to save session. Please try again.')
      setStep('review')
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Input step
  if (step === 'input') {
    return (
      <div className="page">
        <CrisisOverlay
          isOpen={showCrisisOverlay}
          onClose={() => setShowCrisisOverlay(false)}
          detectedKeyword={crisisKeyword || undefined}
        />
        <div className="page-header">
          <Link to="/" style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>
            ← Back
          </Link>
          <h1 className="page-title" style={{ marginTop: '0.5rem' }}>Quick Reframe</h1>
        </div>

        <div className="page-content">
          {/* Mode toggle */}
          <div className="mode-toggle">
            <button
              className={`mode-btn ${mode === 'voice' ? 'active' : ''}`}
              onClick={() => setMode('voice')}
            >
              Voice
            </button>
            <button
              className={`mode-btn ${mode === 'text' ? 'active' : ''}`}
              onClick={() => setMode('text')}
            >
              Text
            </button>
          </div>

          {mode === 'voice' ? (
            <div className="record-button-container">
              <ContextualHint id={HINTS.NEW_SESSION_VOICE.id} icon={HINTS.NEW_SESSION_VOICE.icon}>
                {HINTS.NEW_SESSION_VOICE.content}
              </ContextualHint>
              <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', marginBottom: '1rem' }}>
                Describe what's on your mind. What happened, how you're feeling, and what thoughts came up.
              </p>

              <button
                className={`btn btn-record ${isRecording ? 'recording' : ''}`}
                onClick={isRecording ? stopRecording : startRecording}
              >
                {isRecording ? 'Stop' : 'Record'}
              </button>

              <p className={`record-status ${isRecording ? 'recording' : ''}`}>
                {isRecording ? formatTime(recordingTime) : 'Tap to start'}
              </p>
            </div>
          ) : (
            <div>
              <ContextualHint id={HINTS.NEW_SESSION_TEXT.id} icon={HINTS.NEW_SESSION_TEXT.icon}>
                {HINTS.NEW_SESSION_TEXT.content}
              </ContextualHint>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
                Describe what's on your mind. What happened, how you're feeling, and what thoughts came up.
              </p>

              <div className="form-group">
                <textarea
                  className="form-textarea"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Example: I have a presentation tomorrow and I'm convinced I'll mess it up. My chest feels tight and I can't stop thinking about all the ways it could go wrong..."
                  rows={6}
                />
              </div>

              <button
                className="btn btn-primary"
                style={{ width: '100%' }}
                onClick={processText}
                disabled={!textInput.trim()}
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Processing step
  if (step === 'processing') {
    return (
      <div className="page">
        <div className="page-content">
          <Loading message="Processing your thoughts..." />
        </div>
      </div>
    )
  }

  // Review step
  if (step === 'review') {
    return (
      <div className="page">
        <CrisisOverlay
          isOpen={showCrisisOverlay}
          onClose={() => setShowCrisisOverlay(false)}
          detectedKeyword={crisisKeyword || undefined}
        />
        <div className="page-header">
          <button
            onClick={() => setStep('input')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-muted)',
              cursor: 'pointer',
              padding: 0
            }}
          >
            ← Edit
          </button>
          <h1 className="page-title" style={{ marginTop: '0.5rem' }}>Review & Save</h1>
        </div>

        <div className="page-content" style={{ paddingBottom: '100px' }}>
          {/* Trigger */}
          <div className="form-group">
            <label className="form-label">What triggered this?</label>
            <textarea
              className="form-textarea"
              value={session.trigger_text || ''}
              onChange={(e) => setSession(s => ({ ...s, trigger_text: e.target.value }))}
              rows={2}
            />
          </div>

          {/* Auto thought */}
          <div className="form-group">
            <label className="form-label">Automatic thought</label>
            <textarea
              className="form-textarea"
              value={session.auto_thought || ''}
              onChange={(e) => setSession(s => ({ ...s, auto_thought: e.target.value }))}
              rows={2}
            />
          </div>

          {/* Emotion */}
          <div className="form-group">
            <label className="form-label">Emotion</label>
            <input
              type="text"
              className="form-input"
              value={session.emotion_label || ''}
              onChange={(e) => setSession(s => ({ ...s, emotion_label: e.target.value }))}
              placeholder="e.g., anxious, frustrated, sad"
            />
          </div>

          {/* Intensity before */}
          <EmotionSlider
            label="Intensity (before)"
            value={session.emotion_intensity_pre || 5}
            onChange={(v) => setSession(s => ({ ...s, emotion_intensity_pre: v }))}
          />

          {/* Evidence for */}
          <div className="form-group">
            <label className="form-label">Evidence supporting the thought</label>
            <textarea
              className="form-textarea"
              value={session.evidence_for?.join('\n') || ''}
              onChange={(e) => setSession(s => ({
                ...s,
                evidence_for: e.target.value.split('\n').filter(Boolean)
              }))}
              placeholder="One per line"
              rows={2}
            />
          </div>

          {/* Evidence against */}
          <div className="form-group">
            <label className="form-label">Evidence against the thought</label>
            <textarea
              className="form-textarea"
              value={session.evidence_against?.join('\n') || ''}
              onChange={(e) => setSession(s => ({
                ...s,
                evidence_against: e.target.value.split('\n').filter(Boolean)
              }))}
              placeholder="One per line"
              rows={2}
            />
          </div>

          {/* Balanced thought */}
          <div className="form-group">
            <label className="form-label">Balanced thought</label>
            <textarea
              className="form-textarea"
              value={session.balanced_thought || ''}
              onChange={(e) => setSession(s => ({ ...s, balanced_thought: e.target.value }))}
              rows={3}
            />
          </div>

          {/* Action step */}
          <div className="form-group">
            <label className="form-label">Action step</label>
            <input
              type="text"
              className="form-input"
              value={session.action_step || ''}
              onChange={(e) => setSession(s => ({ ...s, action_step: e.target.value }))}
              placeholder="One small thing you can do"
            />
          </div>

          {/* Intensity after */}
          <EmotionSlider
            label="Intensity (after reflection)"
            value={intensityPost}
            onChange={setIntensityPost}
          />

          {/* Distortions */}
          <div className="form-group">
            <label className="form-label">Cognitive distortions</label>
            <ContextualHint id={HINTS.REVIEW_DISTORTIONS.id} icon={HINTS.REVIEW_DISTORTIONS.icon}>
              {HINTS.REVIEW_DISTORTIONS.content}
            </ContextualHint>
            <div className="distortion-tags">
              {DISTORTIONS.map(d => (
                <button
                  key={d}
                  className={`distortion-tag ${session.distortions?.includes(d) ? 'selected' : ''}`}
                  onClick={() => toggleDistortion(d)}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Save button */}
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '1rem',
          background: 'linear-gradient(transparent, var(--color-bg) 20%)',
        }}>
          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={saveSession}
          >
            Save Session
          </button>
        </div>
      </div>
    )
  }

  // Saving step
  return (
    <div className="page">
      <div className="page-content">
        <Loading message="Saving your session..." />
      </div>
    </div>
  )
}
